import FlashPCBMarkdown from "../FlashPCBMarkdown";

import { CapabilitiesPageProps } from "./constants";
import Button from "../common/Button";
import { UPLOAD_BOARD } from "../../constants/routes";
import { useRouter } from "next/router";
import React from "react";
import {
  dateStringToDate,
  dateStringToHumanReadableDate,
  twentyFourHourToTwelveHour,
} from "../../utils/valueFormatting";
import {
  Container,
  Body,
  Title,
  Description,
  TitleBlock,
  Options,
  Tables,
  OptionHeader,
  OptionCard,
  ButtonWrapper,
  CapacityExplanation,
  SmallerOptionText,
  ListItem,
} from "./CapabilitiesPageStyles";
import { Header3 } from "../FlashPCBMarkdown/FlashPCBMarkdownStyles";

const CapabilitiesPage = ({
  markdown,
  leadTimeOptions,
  datesClosed,
  flashPCBSettings,
}: CapabilitiesPageProps) => {
  const router = useRouter();

  const now = new Date();
  const midnightToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    0,
    0,
    0,
    0,
  );

  return (
    <Container>
      <Body>
        <TitleBlock>
          <Title>Our Capabilities</Title>
        </TitleBlock>
        <Header3 id={"lead-time"}>Lead Times</Header3>
        <Description>
          We offer {leadTimeOptions.length} base options for manufacturing lead
          times:
        </Description>
        <Options>
          {leadTimeOptions.map((option) => {
            return (
              <OptionCard key={option.ship_date} width={"300px"}>
                <OptionHeader disabled={option.disabled}>
                  {option.days} days
                </OptionHeader>
                <SmallerOptionText disabled={option.disabled}>
                  ships
                </SmallerOptionText>
                <OptionHeader disabled={option.disabled}>
                  {dateStringToHumanReadableDate(
                    option.ship_date as string,
                    true,
                  )}
                </OptionHeader>
                {option.disabled && (
                  <CapacityExplanation>At capacity</CapacityExplanation>
                )}
              </OptionCard>
            );
          })}
        </Options>
        <Description>
          Orders must be placed before{" "}
          {twentyFourHourToTwelveHour(flashPCBSettings.local_lead_time_eod)}{" "}
          Eastern time (local time in Philadelphia, PA) in order for the lead
          time to start that day.
        </Description>
        <Header3>Office Closures</Header3>
        <Description>
          The FlashPCB office is scheduled to be closed on the following days:
          <ul>
            {datesClosed
              .filter((dateClosed) => {
                return dateStringToDate(dateClosed.date) >= midnightToday;
              })
              .map((dateClosed) => {
                return (
                  <ListItem key={dateClosed.date}>
                    {dateStringToHumanReadableDate(dateClosed.date)} (
                    {dateClosed.description || "FlashPCB Holiday"})
                  </ListItem>
                );
              })}
          </ul>
        </Description>
        <Header3>What Impacts Lead Time</Header3>
        <Description>
          <ul>
            <ListItem>
              Cleaning (required for boards with through hole components) adds 1
              day
            </ListItem>
            <ListItem>Every 70 unique parts on a board adds 1 day</ListItem>
          </ul>
        </Description>
        <Header3>Quote Options</Header3>
        <Description>
          We provide two ways to get your board assembled:
        </Description>
        <Options>
          <OptionCard width={"400px"}>
            <OptionHeader>Instant Quote</OptionHeader>
            <SmallerOptionText>
              Upload your board and get a quote in minutes
            </SmallerOptionText>
            <ButtonWrapper>
              <Button
                centerText
                label="Order board"
                leftIconProps={{
                  src: "/icons/wrench.svg",
                  alt: "order-board",
                  iconDimension: 24,
                }}
                onClick={() => void router.push(UPLOAD_BOARD)}
              />
            </ButtonWrapper>
          </OptionCard>
          <OptionCard width={"400px"}>
            <OptionHeader>Email Quote</OptionHeader>
            <SmallerOptionText>
              Email us at{" "}
              <a href="mailto:quote@flashpcb.com">quote@flashpcb.com</a> to get
              access to our expanded range of capabilities
            </SmallerOptionText>
            <ButtonWrapper>
              <Button
                centerText
                label="Email us"
                leftIconProps={{
                  src: "/icons/upload.svg",
                  alt: "order-board",
                  iconDimension: 24,
                }}
                onClick={() => void router.push("mailto:quote@flashpcb.com")}
              />
            </ButtonWrapper>
          </OptionCard>
        </Options>
        <Header3>Our certifications</Header3>
        <Description>
          Adherence to stringent standards and certifications, including
          J-STD-001/IPC-A-610
        </Description>
        <Tables id={"specs"}>
          <FlashPCBMarkdown markdown={markdown} prettyTable />
        </Tables>
      </Body>
    </Container>
  );
};

export default CapabilitiesPage;
